import BigNumber from 'bignumber.js'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Image from 'UIKit/Image/Image'
import OpenEffect from 'UIKit/OpenEffect'
import Text from 'UIKit/Text/Text'
import tokens, { USD_CURRENCY } from 'config/constants/tokens'
import { TokenAmount } from 'config/types'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import { useIsomorphicEffect } from 'hooks/useIsomorphicEffect'
import React, { useState } from 'react'
import { Trans } from 'react-i18next'
import BonusService from 'services/BonusService'
import styled from 'styled-components'
import { getFullDisplayBalance } from 'utils/formatBalance'

interface BonusCancelModalContentProps {
  bonus: WageringConditionBonus
}

const BonusCancelConfirmationModalContent: React.FC<BonusCancelModalContentProps> = ({ bonus }) => {
  const [deductedAmount, setDeductedAmount] = useState<TokenAmount>(null)

  useIsomorphicEffect(() => {
    const fetch = async () => {
      const response = await BonusService.getDeductBalanceWhenCancel(bonus.id)
      if (response?.token?.token) {
        setDeductedAmount({
          amount: new BigNumber(response.amount),
          token: tokens[response.token.network][response.token.token],
        })
      }
    }

    if (bonus.isCreaditReward) {
      fetch()
    }
  }, [bonus.id, bonus.isCreaditReward])

  return (
    <OpenEffect>
      <Text color="textSubtle" fontSize="12px" lineHeight="normal" fontWeight="300">
        {bonus.isCreaditReward ? (
          deductedAmount && (
            <Trans>
              By cancelling this active bonus, the bonus amount will be removed, and you will forfeit any winnings
              related to this reward. Your remaining balance will consist of your initial deposit amount minus any bets
              placed. This means{' '}
              {{
                deductedAmount: `${getFullDisplayBalance(deductedAmount.amount, 0, 5)} ${deductedAmount.token.name} `,
              }}{' '}
              will be deducted from your current balance.
            </Trans>
          )
        ) : (
          <Trans>
            Canceling now will remove your bonus, and this action cannot be undone. Are you sure you want to cancel?
          </Trans>
        )}
      </Text>

      {bonus.bonusAmount && bonus.bonusAmount.amount.gt(0) && !bonus.isCreaditReward && (
        <StyledYourBonusModal mt="20px">
          <Flex justifyContent="center" alignItems="center">
            <Text fontSize="12px" fontWeight="600">
              <Trans>Your bonus</Trans>:
            </Text>
            <Flex ml="8px" justifyContent="center" alignItems="center">
              <Box width={20} height={20}>
                {bonus.bonusAmount.token === USD_CURRENCY ? (
                  <Image src={bonus.bonusAmount.token.logo} width={46} height={46} alt="usd icon" />
                ) : (
                  <Image src={bonus.bonusAmount.token.logo} width={20} height={20} alt="logo-your-bonus" />
                )}
              </Box>
              <Text ml="5px" mr="5px" fontWeight="700" fontSize="20px">
                {(bonus as WageringConditionBonus)?.isActiveMilestone
                  ? getFullDisplayBalance(
                      bonus?.milestones?.reduce?.(
                        (result, milestone) => result.plus(!milestone.isClaimed ? milestone.bonusAmount : 0),
                        new BigNumber(0),
                      ) || bonus.bonusAmount.amount,
                      0,
                      6,
                    )
                  : bonus.bonusAmount.amount.toString()}
              </Text>
            </Flex>
          </Flex>
        </StyledYourBonusModal>
      )}
    </OpenEffect>
  )
}

const StyledYourBonusModal = styled(Box)`
  background: rgba(255, 255, 255, 0.04);
  padding: 12px 8px;
  border-radius: 4px;
`

export default BonusCancelConfirmationModalContent
